/**
 * What the Printable Bracket Maker hands over to the Bracket widget, and what
 * it keeps in the browser so a refresh does not wipe somebody's draw.
 *
 * The widget reads this shape in CommonNinja/apps at
 * `packages/apps/bracket/src/bridge/bridge.types.ts`. Keep the two in step.
 *
 * WHY THE PAYLOAD IS THIS SMALL. The tool makes a sheet of paper: round one
 * seeded, every later round blank. There are no results to carry because the
 * tool never records any, and there is no styling to carry because the widget
 * has its own skins. So the honest payload is the draw and nothing else.
 */

export const BRACKET_TOOL_SLUG = 'printable-bracket-maker';
export const BRACKET_WIDGET_SLUG = 'brackets';

/**
 * Tells the Bracket widget not to open its "choose a bracket type" popup.
 *
 * That popup renders on every brand-new widget, so it lands on top of a draw
 * that has just been handed over, and completing it runs
 * `mergeBracketWithFormData()`, which rebuilds the participant list as
 * "Participant 1..4" and writes it over the imported names.
 *
 * A dedicated flag rather than reusing `bridge`: Daniel's call 2026-09-16, and
 * it means the widget never has to know what the bridge is. Any later flow that
 * arrives with its content already chosen can pass the same flag.
 *
 * ⚠️ The widget half reads this in CommonNinja/apps at
 * `packages/apps/bracket/src/components/editor/common/startTournament/startTournament.comp.tsx`.
 * Both sides must agree on the spelling.
 */
export const BRACKET_HIDE_QUICK_START_PARAM = 'hideQuickStart';
export const BRACKET_HANDOVER_PARAMS: Record<string, string> = {
  [BRACKET_HIDE_QUICK_START_PARAM]: '1',
};

import type { TBracketFormat, TDrawOrder, TBracketSize } from './bracketMath';

export interface IBracketBridgeParticipant {
  /** Display name. Text only: the tool has no image support, by design. */
  name: string;
  /**
   * Seed, when the visitor chose a seeded draw. The widget's own
   * `GroupsHelper` reads `ranking` to place participants, so this maps
   * straight across.
   */
  seed: number | null;
}

export interface IBracketBridgePayload {
  title: string;
  /**
   * ALREADY IN FINAL DRAW ORDER, top line of the sheet first.
   *
   * This matters. The widget can order participants itself by index, at random,
   * or by seeding, but re-running a random draw on the widget side would hand
   * the visitor a DIFFERENT bracket from the one they just printed. So the tool
   * resolves the order first and the adapter is told to keep it (`orderBy` of
   * `index`), which makes the carried bracket identical to the sheet.
   */
  participants: IBracketBridgeParticipant[];
  format: TBracketFormat;
  size: TBracketSize;
  thirdPlace: boolean;
  /**
   * The sheet background, as a `#rrggbb` hex, when the visitor picked one.
   *
   * ⚠️ Added 2026-09-22. Sergei: "the background, for example, isn't
   * transferred". It was not, because the payload deliberately carried the draw
   * and nothing else, on the reasoning that the widget has its own skins. That
   * reasoning holds for the PRINT styles, which are paper presets (black lines
   * for a pen), but not for a colour somebody deliberately chose and can see on
   * both sides.
   *
   * NULL WHEN UNTOUCHED, and that is load-bearing. The tool opens on white, so
   * always sending a colour would paint every handed-over widget white and
   * throw away the skin's own background for the many visitors who never opened
   * the style panel. Only a deliberate choice travels.
   *
   * ⛔ The background IMAGE does not travel. It is a file in the visitor's
   * browser, so it needs the IndexedDB carry plus a sign-in-time upload that
   * `tier-list` already has and `bracket` does not. Separate job, not a
   * forgotten field.
   */
  background?: string | null;
}

/**
 * The saved draft is the payload plus the things that only matter on paper.
 * `draftToPayload` picks fields explicitly, so the print-only settings can
 * never leak across the bridge.
 */
export type IBracketDraft = IBracketBridgePayload & {
  /** Print preset. Tool-side presentation only, never sent to the widget. */
  theme?: string;
  /** The draw the visitor picked, kept so the builder can show it again. */
  order?: TDrawOrder;
};

const MAX_TITLE = 120;
const MAX_NAME = 60;
/**
 * ⚠️ This is a CLAMP on untrusted input, not a statement of what the tool
 * supports. It sat at 32 while `/64-team-bracket` was handing over a 64-name
 * draw, so half the field was dropped silently on the one page named after its
 * size. The widget side has always allowed 64
 * (`packages/apps/bracket/src/bridge/bridge.adapter.ts`), and the widget core
 * enforces no maximum at all, so 32 was the tighter of the two and it won.
 * Keep the clamp, keep it at the number the tool can actually produce.
 */
const MAX_PARTICIPANTS = 64;

/**
 * The draft comes out of the visitor's own storage, so it is untrusted input by
 * the time it reaches here: clamp every length and count, and never trust the
 * shape. A malformed draft must produce an empty payload, which opens a normal
 * empty editor, rather than an error the visitor sees.
 */
export function draftToPayload(
  draft: IBracketDraft | null
): IBracketBridgePayload {
  const empty: IBracketBridgePayload = {
    title: '',
    participants: [],
    format: 'single',
    size: 4,
    thirdPlace: false,
    background: null,
  };

  if (!draft || typeof draft !== 'object') return empty;

  const participants = Array.isArray(draft.participants)
    ? draft.participants
        .filter((p) => p && typeof p.name === 'string' && p.name.trim())
        .slice(0, MAX_PARTICIPANTS)
        .map((p) => ({
          name: String(p.name).trim().slice(0, MAX_NAME),
          seed:
            typeof p.seed === 'number' && Number.isFinite(p.seed)
              ? Math.max(1, Math.min(MAX_PARTICIPANTS, Math.round(p.seed)))
              : null,
        }))
    : [];

  // A colour off our own swatch list, or nothing. Untrusted input, so it is
  // matched rather than trusted: anything else is dropped and the widget keeps
  // its skin.
  const background =
    typeof draft.background === 'string' &&
    /^#[0-9a-f]{6}$/i.test(draft.background.trim())
      ? draft.background.trim().toLowerCase()
      : null;

  return {
    title: typeof draft.title === 'string' ? draft.title.slice(0, MAX_TITLE) : '',
    participants,
    background,
    format: draft.format === 'double' ? 'double' : 'single',
    // 64 belongs here for the same reason. The adapter derives the bracket from
    // the participant list and never reads `size`, so omitting it broke nothing
    // visible, but a 64-player draw was travelling with `size: 4` attached,
    // which is false data sitting in a versioned cross-repo contract.
    size: ([4, 8, 16, 32, 64] as number[]).includes(draft.size as number)
      ? draft.size
      : 4,
    thirdPlace: draft.thirdPlace === true,
  };
}
